/* =========================================================================
   lapse-render.js — gemeinsamer Zeichen-Code für Konfigurator und Druck-Tool
   -------------------------------------------------------------------------
   Diese Datei ist die EINZIGE Stelle, an der das Poster gezeichnet wird.
   Der Konfigurator (Vorschau) und das lokale Druck-Tool verwenden exakt
   denselben Code. Wer das Design ändert, erhöht VERSION und ergänzt die
   neue Version im Druck-Tool – alte Bestellungen werden weiterhin mit der
   Version gedruckt, mit der sie bestellt wurden.

   Version 2 (16.09.2026) – Schärfe-Überarbeitung nach Gelato-Muster:
   - Zoomstufen neu: Nah = bisher Mittel (6,5 m/mm), Mittel = bisher Weit
     (11 m/mm), Weit neu (18 m/mm). Historische Karten werden damit nie mehr
     über ihre Scan-Auflösung (0,5 m/px) hinaus vergrössert.
   - Druck: historische Karten in nativer swisstopo-Auflösung laden, alle
     Karten mit Lanczos-3 exakt auf die Druckpixel umrechnen (reine
     Neuabtastung, keine Formänderung).
   - Druck: Kartenfelder auf ganze Druckpixel ausgerichtet, damit das Bild
     1:1 und ohne zusätzliche Weichzeichnung auf das Poster kommt.
   ========================================================================= */
(function (root) {
'use strict';

const VERSION = 2;

/* ---------- Produkt ---------- */
const FORMATS = {
  '50x70-quer': {w:700, h:500, label:'50 × 70', sub:'quer'},
  '50x70-hoch': {w:500, h:700, label:'50 × 70', sub:'hoch'},
  '30x40-quer': {w:400, h:300, label:'30 × 40', sub:'quer'},
  '30x40-hoch': {w:300, h:400, label:'30 × 40', sub:'hoch'},
};
// m = Meter Gelände pro mm Papier
const ZOOM = { nah:{m:6.5, label:'Nah'}, mittel:{m:11, label:'Mittel'}, weit:{m:18, label:'Weit'} };
const DESIGNS = { chronik:{label:'Chronik'}, galerie:{label:'Galerie'}, editorial:{label:'Editorial'} };
const COLOURS = { sand:{label:'Sand', bg:'#F4F0E8', ink:'#22211E'},
                  negativ:{label:'Negativ', bg:'#17181A', ink:'#EDE7DA'} };
const PINS = { nadel:'Stecknadel', tropfen:'Kartennadel', kreis:'Kreis' };
const PIN_COLOURS = { '#FFFFFF':'Weiss', '#1A1917':'Schwarz', '#C8412A':'Rot', '#C9A227':'Gold' };
const YEARS = [1926, 1976, 2026];

// Schriftgrössen in mm bei 50×70; k skaliert sie auf andere Formate
const TYPO = {
  chronik:  {title:43.8, sub:4.6, year:23.3, foot:3.3,
             titleFont:'"Instrument Serif", Georgia, serif', titleWeight:'',
             yearFont:'"Instrument Serif", Georgia, serif', yearWeight:'',
             keyline:true, rule:true},
  galerie:  {title:21.0, sub:4.9, year:6.0,  foot:3.2,
             titleFont:'"Instrument Sans", sans-serif', titleWeight:'600 ',
             yearFont:'"Instrument Sans", sans-serif', yearWeight:'500 ',
             keyline:true, rule:false},
  editorial:{title:31.0, sub:4.8, year:15.0, foot:3.3,
             titleFont:'"Young Serif", Georgia, serif', titleWeight:'',
             yearFont:'"Outfit", system-ui, sans-serif', yearWeight:'',
             keyline:false, rule:false},
};
const FONT_SPECS = ['20px "Instrument Serif"', '400 20px "Instrument Sans"', '500 20px "Instrument Sans"',
                    '600 20px "Instrument Sans"', '20px "Young Serif"', '20px "Outfit"'];

/* ---------- Kartenstufen ----------
   - pixelkarte-grau (2026): ab 2 m/px die generalisierte Karte mit grossen
     Beschriftungen (bewusst gewählter Stil). Wird immer mit REF_MPP geladen
     und für den Druck mit Lanczos hochgerechnet.
   - zeitreihen (1926/1976): feinste echte Auflösung 0,5 m/px. Druck lädt die
     gröbste native Stufe, die noch mindestens so fein ist wie der Druck.
   Die Tonwertkurve wird IMMER aus dem REF_MPP-Bild berechnet, damit Vorschau
   und Druck exakt dieselben Grauwerte bekommen. */
const REF_MPP = 2.0;
const NATIVE_MPP = [0.5, 1.0, 1.5, 2.0];
const WMS_MAX = 10000;  // grösste getestete WMS-Bildseite
const WMS = 'https://wms.geo.admin.ch/?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&STYLES='
          + '&FORMAT=image/png&CRS=EPSG:2056';
const ZIEL = 0.95;
const BLEED_MM = 4;     // Gelato: 4 mm Beschnittzugabe pro Seite

/* =========================================================================
   Druckcode: exakter Zustand aus dem Konfigurator, versioniert
   ========================================================================= */
function b64urlEncode(str){
  const bytes = new TextEncoder().encode(str);
  let bin = ''; bytes.forEach(b => bin += String.fromCharCode(b));
  return btoa(bin).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
}
function b64urlDecode(s){
  s = s.replace(/-/g,'+').replace(/_/g,'/'); while (s.length % 4) s += '=';
  const bin = atob(s); const bytes = new Uint8Array(bin.length);
  for (let i=0;i<bin.length;i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}
function encode(spec){
  const o = { v:VERSION, E:+spec.E.toFixed(2), N:+spec.N.toFixed(2), t:spec.titel, o:spec.ort,
              f:spec.fmt, d:spec.design, z:spec.zoom, c:spec.colour,
              p:spec.pin ? spec.pinKind : null, pc:spec.pin ? spec.pinColour : null, a:spec.label || '' };
  return 'LAPSE' + VERSION + '.' + b64urlEncode(JSON.stringify(o));
}
function decode(code){
  const m = String(code).trim().match(/^LAPSE(\d+)\.([A-Za-z0-9_-]+)$/);
  if (!m) throw new Error('Kein gültiger Druckcode (erwartet "LAPSE<Version>.<Daten>").');
  const o = JSON.parse(b64urlDecode(m[2]));
  if (+m[1] !== o.v) throw new Error('Versionsangabe im Druckcode ist widersprüchlich.');
  return { version:o.v, E:o.E, N:o.N, titel:o.t, ort:o.o, fmt:o.f, design:o.d, zoom:o.z, colour:o.c,
           pin:!!o.p, pinKind:o.p || 'tropfen', pinColour:o.pc || '#C8412A', label:o.a || '' };
}
function validate(spec){
  const err = [];
  if (!FORMATS[spec.fmt]) err.push('Format unbekannt: ' + spec.fmt);
  if (!DESIGNS[spec.design]) err.push('Design unbekannt: ' + spec.design);
  if (!ZOOM[spec.zoom]) err.push('Ausschnitt unbekannt: ' + spec.zoom);
  if (!COLOURS[spec.colour]) err.push('Farbe unbekannt: ' + spec.colour);
  if (spec.pin && !PINS[spec.pinKind]) err.push('Markierung unbekannt: ' + spec.pinKind);
  if (spec.pin && !Object.keys(PIN_COLOURS).some(h => h.toLowerCase() === String(spec.pinColour).toLowerCase()))
    err.push('Markierungsfarbe unbekannt: ' + spec.pinColour);
  if (!(spec.E > 2480000 && spec.E < 2840000 && spec.N > 1070000 && spec.N < 1300000))
    err.push('Koordinaten ausserhalb der Schweiz.');
  return err;
}

/* =========================================================================
   Geometrie (alles in mm Papier, ohne Beschnitt)
   ========================================================================= */
function geometry(spec){
  const F = FORMATS[spec.fmt], k = Math.min(F.w,F.h)/500, m = 30*k;
  const stacked = F.h > F.w, d = spec.design, T = TYPO[d];
  const headH = (T.sub*.72 + 7 + T.title*.70 + 16) * k, footH = 26*k, gap = 10*k;
  let px, py, pw, ph, lab = 0;
  if (d === 'editorial' && stacked){ lab = Math.max(0.17*F.w, 26*k); }
  if (stacked){
    const yrH = (d === 'editorial') ? 0 : (T.year*.72 + 5) * k;
    pw = F.w - 2*m - lab; px = () => m + lab;
    const slot = (F.h - m - headH - footH - m - 2*gap) / 3;
    ph = slot - yrH;
    py = i => m + headH + i*(slot + gap);
  } else {
    pw = (F.w - 2*m - 2*gap)/3;
    const yrH = (T.year*.72 + 5) * k;
    ph = F.h - m - headH - footH - m - yrH;
    px = i => m + i*(pw+gap);
    py = () => m + headH;
  }
  return {F,k,m,stacked,pw,ph,px,py,lab,headH,footH,gap,T};
}

/* Kartenausschnitt in Metern um die Adresse */
function extent(spec){
  const g = geometry(spec), s = ZOOM[spec.zoom].m;
  return { hw: g.pw*s/2, hh: g.ph*s/2, g };
}

/* Kartenfeld i in ganzen Gerätepixeln (Druck). Dieselbe Rundung wird beim
   Laden und beim Zeichnen verwendet, damit das Bild exakt 1:1 passt. */
function panelPx(g, i, pxPerMm, bleed){
  const x0 = Math.round((g.px(i) + bleed) * pxPerMm), y0 = Math.round((g.py(i) + bleed) * pxPerMm);
  const x1 = Math.round((g.px(i) + g.pw + bleed) * pxPerMm), y1 = Math.round((g.py(i) + g.ph + bleed) * pxPerMm);
  return { x:x0, y:y0, w:x1-x0, h:y1-y0 };
}

/* =========================================================================
   Karten laden
   mode 'preview': alle Jahre mit REF_MPP
   mode 'print'  : exakt in Druckpixeln (opts.dpi, opts.bleed); 1926/1976 aus
                   nativer Auflösung, 2026 aus REF_MPP, beides mit Lanczos
   ========================================================================= */
function loadImage(url){
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => res(img);
    img.onerror = () => rej(new Error('Karte konnte nicht geladen werden'));
    img.src = url;
  });
}
function mapUrl(year, spec, mpp){
  const {hw, hh} = extent(spec);
  const W = Math.max(50, Math.round(2*hw/mpp)), H = Math.max(50, Math.round(2*hh/mpp));
  const layer = year >= 2026 ? 'ch.swisstopo.pixelkarte-grau' : 'ch.swisstopo.zeitreihen';
  const time  = year >= 2026 ? '' : `&TIME=${year}1231`;
  const E = spec.E, N = spec.N;
  return `${WMS}&LAYERS=${layer}${time}&BBOX=${E-hw},${N-hh},${E+hw},${N+hh}&WIDTH=${W}&HEIGHT=${H}`;
}
/* Flächen ohne Kartendaten (z.B. ausserhalb der Schweiz) sind im PNG transparent.
   Auf Weiss setzen, sonst würden sie als schwarze Fläche gedruckt. */
function toCanvas(img){
  const c = document.createElement('canvas');
  c.width = img.naturalWidth || img.width; c.height = img.naturalHeight || img.height;
  const x = c.getContext('2d', {willReadFrequently:true});
  x.fillStyle = '#FFFFFF'; x.fillRect(0, 0, c.width, c.height);
  x.drawImage(img, 0, 0);
  return c;
}
/* Tonwertkurve aus Referenzbild (Graustufen, 0,5 % Schwarzpunkt, Median → ZIEL) */
function computeLut(canvas){
  const x = canvas.getContext('2d', {willReadFrequently:true});
  const px = x.getImageData(0,0,canvas.width,canvas.height).data, hist = new Uint32Array(256);
  for (let i=0;i<px.length;i+=4) hist[(px[i]*.299 + px[i+1]*.587 + px[i+2]*.114) | 0]++;
  const n = px.length/4;
  let acc = 0, lo = 0, med = 255;
  for (let v=0; v<256; v++){ acc += hist[v]; if (acc >= n*0.005){ lo = v; break; } }
  acc = 0;
  for (let v=0; v<256; v++){ acc += hist[v]; if (acc >= n*0.5){ med = v; break; } }
  const hi = Math.min(255, lo + Math.max(med-lo,1)/ZIEL);
  const lut = new Uint8Array(256);
  for (let v=0; v<256; v++) lut[v] = Math.max(0, Math.min(255, Math.round((v-lo)/Math.max(hi-lo,1)*255)));
  return lut;
}
/* Graustufen + Kurve; liefert je eine normale und eine invertierte Fassung
   (Invertierung in den Pixeln statt ctx.filter – funktioniert auch in Safari) */
function applyLut(canvas, lut){
  const x = canvas.getContext('2d', {willReadFrequently:true});
  const d = x.getImageData(0,0,canvas.width,canvas.height), px = d.data;
  const inv = new ImageData(new Uint8ClampedArray(px.length), canvas.width, canvas.height), q = inv.data;
  for (let i=0;i<px.length;i+=4){
    const g = lut[(px[i]*.299 + px[i+1]*.587 + px[i+2]*.114) | 0];
    px[i]=px[i+1]=px[i+2]=g; px[i+3]=255;
    q[i]=q[i+1]=q[i+2]=255-g; q[i+3]=255;
  }
  x.putImageData(d,0,0);
  const c2 = document.createElement('canvas'); c2.width = canvas.width; c2.height = canvas.height;
  c2.getContext('2d').putImageData(inv,0,0);
  return { normal:canvas, negativ:c2 };
}

/* Luminanz eines Canvas als Float32Array */
function luma(canvas){
  const px = canvas.getContext('2d', {willReadFrequently:true}).getImageData(0,0,canvas.width,canvas.height).data;
  const out = new Float32Array(canvas.width*canvas.height);
  for (let i=0, j=0; j<out.length; i+=4, j++) out[j] = px[i]*.299 + px[i+1]*.587 + px[i+2]*.114;
  return out;
}
/* Lanczos-3, getrennt in x und y; funktioniert für Ver- und Hochrechnung */
function lanczosWeights(sn, dn){
  const a = 3, scale = dn / sn, f = Math.min(1, scale), support = a / f;
  const idx = [], wts = [];
  for (let d=0; d<dn; d++){
    const c = (d + 0.5) / scale - 0.5;
    const i0 = Math.floor(c - support) + 1, i1 = Math.floor(c + support);
    const ii = [], ww = []; let sum = 0;
    for (let i=i0; i<=i1; i++){
      const t = (i - c) * f; let w;
      if (t === 0) w = 1;
      else if (Math.abs(t) >= a) w = 0;
      else { const p = Math.PI*t; w = a*Math.sin(p)*Math.sin(p/a)/(p*p); }
      if (w === 0) continue;
      ii.push(Math.min(sn-1, Math.max(0, i))); ww.push(w); sum += w;
    }
    idx.push(Int32Array.from(ii)); wts.push(Float32Array.from(ww.map(w => w/sum)));
  }
  return {idx, wts};
}
function resample(src, sw, sh, dw, dh){
  const X = lanczosWeights(sw, dw), Y = lanczosWeights(sh, dh);
  const tmp = new Float32Array(dw*sh);
  for (let y=0; y<sh; y++){
    const row = y*sw, orow = y*dw;
    for (let x=0; x<dw; x++){
      const ii = X.idx[x], ww = X.wts[x]; let s = 0;
      for (let k=0; k<ii.length; k++) s += src[row + ii[k]] * ww[k];
      tmp[orow + x] = s;
    }
  }
  const out = new Float32Array(dw*dh);
  for (let y=0; y<dh; y++){
    const ii = Y.idx[y], ww = Y.wts[y], orow = y*dw;
    for (let x=0; x<dw; x++){
      let s = 0;
      for (let k=0; k<ii.length; k++) s += tmp[ii[k]*dw + x] * ww[k];
      out[orow + x] = s;
    }
  }
  return out;
}
/* Graustufenbild + Kurve in ein Canvas (nur die benötigte Fassung) */
function grayToCanvas(gray, w, h, lut, negativ){
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const x = c.getContext('2d'), d = x.createImageData(w, h), px = d.data;
  for (let j=0, i=0; j<gray.length; j++, i+=4){
    const v = gray[j], g = lut[v < 0 ? 0 : v > 255 ? 255 : (v + 0.5) | 0];
    px[i] = px[i+1] = px[i+2] = negativ ? 255-g : g; px[i+3] = 255;
  }
  x.putImageData(d, 0, 0);
  return c;
}
/* gröbste native Stufe, die mindestens so fein ist wie benötigt */
function nativeMpp(needed, spanM){
  let mpp = NATIVE_MPP[0];
  for (const n of NATIVE_MPP) if (n <= needed + 1e-9) mpp = n;
  while (spanM / mpp > WMS_MAX){
    const next = NATIVE_MPP.find(n => n > mpp);
    if (!next) break; mpp = next;
  }
  return mpp;
}

async function loadMaps(spec, mode, opts){
  opts = opts || {};
  const out = {};
  if (mode !== 'print'){
    await Promise.all(YEARS.map(async y => {
      const ref = toCanvas(await loadImage(mapUrl(y, spec, REF_MPP)));
      out[y] = applyLut(ref, computeLut(ref));
      if (opts.onProgress) opts.onProgress(y);
    }));
    return out;
  }
  const dpi = opts.dpi || 300, pxPerMm = dpi / 25.4;
  const bleed = (opts.bleed === undefined) ? BLEED_MM : opts.bleed;
  const {g, hw, hh} = extent(spec);
  const s = ZOOM[spec.zoom].m, neededMpp = s / pxPerMm;
  const negativ = spec.colour === 'negativ';
  // nacheinander statt parallel: begrenzt den Speicherbedarf bei 50×70
  for (let i = 0; i < YEARS.length; i++){
    const y = YEARS[i], P = panelPx(g, i, pxPerMm, bleed);
    const ref = toCanvas(await loadImage(mapUrl(y, spec, REF_MPP)));
    const lut = computeLut(ref);
    let src = ref, srcMpp = REF_MPP;
    if (y < 2026){
      srcMpp = nativeMpp(neededMpp, 2*Math.max(hw, hh));
      if (srcMpp < REF_MPP) src = toCanvas(await loadImage(mapUrl(y, spec, srcMpp)));
    }
    const gray = resample(luma(src), src.width, src.height, P.w, P.h);
    const c = grayToCanvas(gray, P.w, P.h, lut, negativ);
    out[y] = { normal: negativ ? null : c, negativ: negativ ? c : null, print: true,
               info: { srcMpp, printMpp: neededMpp, factor: srcMpp / neededMpp, w: P.w, h: P.h } };
    if (opts.onProgress) opts.onProgress(y);
  }
  return out;
}

/* =========================================================================
   Zeichnen
   opts.pxPerMm  : Pixel pro mm (Vorschau: Bildschirm, Druck: dpi/25.4)
   opts.bleed    : Beschnittzugabe in mm (Vorschau 0, Druck BLEED_MM)
   opts.watermark: nur Vorschau
   Canvas-Grösse: (F.w + 2*bleed) × (F.h + 2*bleed) mm
   ========================================================================= */
function mix(a,b,t){
  const p = h => [1,3,5].map(i => parseInt(h.slice(i,i+2),16));
  const [r1,g1,b1] = p(a), [r2,g2,b2] = p(b);
  const c = (x,y) => Math.round(x + (y-x)*t);
  return `rgb(${c(r1,r2)},${c(g1,g2)},${c(b1,b2)})`;
}
function fmtCoord(v){ return Math.round(v).toLocaleString('de-CH').replace(/\u00A0/g,'’').replace(/'/g,'’'); }

/* LV95 → WGS84 (Näherungsformel swisstopo, Genauigkeit ~1 m) */
function toWgs84(E, N){
  const y = (E - 2600000) / 1e6, x = (N - 1200000) / 1e6;
  const lon = 2.6779094 + 4.728982*y + 0.791484*y*x + 0.1306*y*x*x - 0.0436*y*y*y;
  const lat = 16.9023892 + 3.238272*x - 0.270978*y*y - 0.002528*x*x - 0.0447*y*y*x - 0.0140*x*x*x;
  return { lat: lat * 100/36, lon: lon * 100/36 };
}
/* GPS-Schreibweise für das Poster, z.B. "47.3777° N · 8.5357° O" */
function fmtGps(E, N){
  const g = toWgs84(E, N);
  return `${g.lat.toFixed(4)}° N · ${g.lon.toFixed(4)}° O`;
}

/* Textbreite skalierungsunabhängig messen (Referenzgrösse 100) */
function textWidth(ctx, fontFn, text, size){
  ctx.font = fontFn(100);
  return ctx.measureText(text).width * size / 100;
}

function draw(ctx, spec, maps, opts){
  const g = geometry(spec), C = COLOURS[spec.colour], T = g.T, k = g.k;
  const bleed = opts.bleed || 0, s = opts.pxPerMm;
  ctx.save();
  ctx.setTransform(s, 0, 0, s, bleed*s, bleed*s);
  ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
  ctx.textBaseline = 'alphabetic';

  ctx.fillStyle = C.bg; ctx.fillRect(-bleed, -bleed, g.F.w + 2*bleed, g.F.h + 2*bleed);

  if (!spec.E){ placeholder(ctx, g, C); ctx.restore(); return; }

  const d = spec.design, mid = g.F.w/2, zentriert = d === 'galerie';

  // Kopf
  const ax = zentriert ? mid : g.m;
  ctx.textAlign = zentriert ? 'center' : 'left';
  ctx.fillStyle = mix(C.ink, C.bg, .45);
  ctx.font = `${T.sub*k}px "Instrument Sans", sans-serif`;
  ctx.fillText((spec.ort || '').toUpperCase(), ax, g.m + T.sub*.72*k);

  let titel = spec.titel || '';
  if (d === 'galerie') titel = titel.toUpperCase();
  const fontFn = sz => `${T.titleWeight}${sz}px ${T.titleFont}`;
  let size = T.title * k;
  while (textWidth(ctx, fontFn, titel, size) > g.F.w - 2*g.m && size > 3) size -= .4;
  ctx.fillStyle = C.ink; ctx.font = fontFn(size);
  const titelBasis = g.m + (T.sub*.72 + 7)*k + T.title*.70*k;
  ctx.fillText(titel, ax, titelBasis);
  if (T.rule){
    ctx.strokeStyle = mix(C.ink, C.bg, .74); ctx.lineWidth = .25;
    ctx.beginPath(); ctx.moveTo(g.m, titelBasis + 6*k); ctx.lineTo(g.F.w - g.m, titelBasis + 6*k); ctx.stroke();
  }

  // Kartenfelder
  YEARS.forEach((y,i) => {
    const x = g.px(i), yy = g.py(i);
    const img = maps && maps[y];
    if (img && img.print) drawPanelPrint(ctx, img, panelPx(g, i, s, bleed), C, spec.colour);
    else drawPanel(ctx, img, x, yy, g.pw, g.ph, C, spec.colour);
    if (T.keyline){
      ctx.strokeStyle = mix(C.ink, C.bg, .72); ctx.lineWidth = .18;
      ctx.strokeRect(x, yy, g.pw, g.ph);
    }
    if (spec.pin) drawPin(ctx, x + g.pw/2, yy + g.ph/2, k, C, spec);

    ctx.fillStyle = C.ink;
    ctx.font = `${T.yearWeight}${T.year*k}px ${T.yearFont}`;
    if (d === 'editorial' && g.stacked){
      ctx.textAlign = 'left';  ctx.fillText(String(y), g.m, yy + T.year*.72*k);
    } else if (d === 'galerie'){
      ctx.textAlign = 'center'; ctx.fillText(String(y), x + g.pw/2, yy + g.ph + (T.year*.72 + 4)*k);
    } else {
      ctx.textAlign = 'left';  ctx.fillText(String(y), x, yy + g.ph + (T.year*.72 + 4)*k);
    }
  });

  // Fuss
  ctx.fillStyle = mix(C.ink, C.bg, .5);
  ctx.font = `${T.foot*k}px "Instrument Sans", sans-serif`;
  const footY = g.F.h - g.m + 2*k;
  ctx.textAlign = 'left';  ctx.fillText('© swisstopo', g.m, footY);
  ctx.textAlign = 'right'; ctx.fillText(fmtGps(spec.E, spec.N), g.F.w - g.m, footY);

  if (opts.watermark) watermark(ctx, g, spec);
  ctx.restore();
}

function drawPanel(ctx, img, x, y, w, h, C, colour){
  if (!img){ ctx.fillStyle = mix(C.ink, C.bg, .88); ctx.fillRect(x,y,w,h); return; }
  ctx.save();
  ctx.beginPath(); ctx.rect(x,y,w,h); ctx.clip();
  ctx.drawImage(colour === 'negativ' ? img.negativ : img.normal, x, y, w, h);
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = colour === 'negativ' ? C.ink : C.bg;
  ctx.fillRect(x,y,w,h);
  ctx.restore();
}
/* Druck: Bild hat exakt die Pixelgrösse des Feldes → 1:1, ohne Skalierung */
function drawPanelPrint(ctx, img, P, C, colour){
  const src = colour === 'negativ' ? img.negativ : img.normal;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(src, P.x, P.y);
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = colour === 'negativ' ? C.ink : C.bg;
  ctx.fillRect(P.x, P.y, P.w, P.h);
  ctx.restore();
}

/* Saum der Markierung: normalerweise in Hintergrundfarbe; ist die Markierung
   selbst zu ähnlich wie der Hintergrund (Weiss auf Sand, Schwarz auf Negativ),
   bekommt sie einen Saum in Schriftfarbe, damit sie sichtbar bleibt. */
function helligkeit(hex){
  const [r,g,b] = [1,3,5].map(i => parseInt(hex.slice(i,i+2),16)/255);
  return 0.299*r + 0.587*g + 0.114*b;
}
function drawPin(ctx, cx, cy, k, C, spec){
  const col = spec.pinColour;
  const saum = Math.abs(helligkeit(col) - helligkeit(C.bg)) < 0.25 ? C.ink : C.bg;
  ctx.save(); ctx.lineJoin = 'round'; ctx.lineCap = 'round';
  if (spec.pinKind === 'tropfen'){
    const r = 4.6*k, h = 13*k;
    const tropfen = () => {
      ctx.beginPath(); ctx.moveTo(cx, cy);
      ctx.quadraticCurveTo(cx - r*1.25, cy - h*0.55, cx - r, cy - h + r);
      ctx.arc(cx, cy - h + r, r, Math.PI, 0);
      ctx.quadraticCurveTo(cx + r*1.25, cy - h*0.55, cx, cy);
      ctx.closePath();
    };
    ctx.strokeStyle = saum; ctx.lineWidth = 1.8*k; tropfen(); ctx.stroke();
    ctx.fillStyle = col; tropfen(); ctx.fill();
    ctx.fillStyle = saum; ctx.beginPath(); ctx.arc(cx, cy - h + r, r*0.42, 0, 7); ctx.fill();
  } else if (spec.pinKind === 'nadel'){
    const kopf = 3.2*k, len = 12*k, sx = cx - len*0.52, sy = cy - len*0.86;
    ctx.strokeStyle = saum; ctx.lineWidth = 2.6*k; ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(sx, sy); ctx.stroke();
    ctx.strokeStyle = col;  ctx.lineWidth = 1.1*k; ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(sx, sy); ctx.stroke();
    ctx.strokeStyle = saum; ctx.lineWidth = 1.8*k; ctx.beginPath(); ctx.arc(sx, sy, kopf, 0, 7); ctx.stroke();
    ctx.fillStyle = col; ctx.beginPath(); ctx.arc(sx, sy, kopf, 0, 7); ctx.fill();
    ctx.fillStyle = saum; ctx.beginPath(); ctx.arc(cx, cy, 0.9*k, 0, 7); ctx.fill();
  } else {
    const r = 6.4*k;
    ctx.strokeStyle = saum; ctx.lineWidth = 3.4*k; ctx.beginPath(); ctx.arc(cx, cy, r, 0, 7); ctx.stroke();
    ctx.strokeStyle = col;  ctx.lineWidth = 1.7*k; ctx.beginPath(); ctx.arc(cx, cy, r, 0, 7); ctx.stroke();
    ctx.fillStyle = col; ctx.beginPath(); ctx.arc(cx, cy, 1.4*k, 0, 7); ctx.fill();
  }
  ctx.restore();
}

/* Wasserzeichen nur in der Vorschau. Zweifarbig (Kontur + Füllung), damit es
   auf hellen und dunklen Kartenflächen gleichermassen sichtbar bleibt. */
function watermark(ctx, g, spec){
  const k = g.k, neg = spec.colour === 'negativ';
  ctx.save();
  ctx.font = `600 ${10*k}px "Instrument Sans", sans-serif`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.lineJoin = 'round';
  ctx.lineWidth = 2.2*k;
  ctx.translate(g.F.w/2, g.F.h/2); ctx.rotate(-Math.PI/7);
  const stepX = 170*k, stepY = 90*k, reach = Math.hypot(g.F.w, g.F.h);
  const zeichne = (fn) => {
    let row = 0;
    for (let y = -reach; y < reach; y += stepY, row++)
      for (let x = -reach; x < reach; x += stepX) fn('LAPSE · VORSCHAU', x + (row % 2 ? stepX/2 : 0), y);
  };
  ctx.globalAlpha = neg ? .45 : .55;
  ctx.strokeStyle = neg ? '#000000' : '#FFFFFF';
  zeichne((t, x, y) => ctx.strokeText(t, x, y));
  ctx.globalAlpha = neg ? .30 : .26;
  ctx.fillStyle = neg ? '#FFFFFF' : '#1A1917';
  zeichne((t, x, y) => ctx.fillText(t, x, y));
  ctx.restore();
}

function placeholder(ctx, g, C){
  ctx.fillStyle = mix(C.ink, C.bg, .86);
  YEARS.forEach((y,i) => ctx.fillRect(g.px(i), g.py(i), g.pw, g.ph));
  ctx.fillStyle = mix(C.ink, C.bg, .5); ctx.textAlign = 'center';
  ctx.font = `${4*g.k}px "Instrument Sans", sans-serif`;
  ctx.fillText('Adresse eingeben', g.F.w/2, g.F.h/2);
}

function loadFonts(){
  if (!document.fonts || !document.fonts.load) return Promise.resolve();
  return Promise.all(FONT_SPECS.map(f => document.fonts.load(f).catch(() => null)));
}

const api = { VERSION, FORMATS, ZOOM, DESIGNS, COLOURS, PINS, PIN_COLOURS, YEARS, TYPO, BLEED_MM, REF_MPP,
              encode, decode, validate, geometry, extent, mapUrl, loadMaps, draw, loadFonts, fmtCoord, fmtGps, toWgs84 };
root.LapseRenderVersions = root.LapseRenderVersions || {};
root.LapseRenderVersions[VERSION] = api;
root.LapseRender = api;       // aktuelle Version
})(window);
